import { Injectable } from '@angular/core';

import { BetslipConfig, QuickDepositConfig } from '@frontend/sports/common/client-config-data-access';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { UserActions, UserService } from '@frontend/sports/user/feature';
import { CashierService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, createSelector } from '@ngrx/store';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';
import { RedirectHelperService } from 'packages/sports/web/app/src/navigation-core/redirect-helper.service';
import { concatMap, filter, from, map, mergeMap, switchMap, tap } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { QuickDepositActions } from '../../quick-deposit/quick-deposit-actions';
import { QuickDepositService } from '../../quick-deposit/services/quick-deposit.service';
import { RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { getSelectedTokenForContext } from '../../reward-tokens/services/linear-reward-tokens.utils';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { selectHasInsufficientMoneyError, selectSlipErrorsState } from '../../validation/selectors';
import BetslipErrorValidatorService from '../../validation/services/betslip-error-validator.service';
import { getStakeUpdateErrors, isBetslipStatusInsufficientMoney } from '../../validation/services/utils/betslip-errors-utils';
import { QuickBetActions } from '../quick-bet-actions';
import { quickBetStateSelector, selectQuickBetContext, selectQuickBetErrorsStateWithRewardsTokens } from '../quick-bet.selectors';
import { IQuickBetErrorsStatus, QuickBetActionButtonState } from '../quick-bet.state';
import { QuickBetUtils } from '../services/quick-bet-utils.service';

const selectActionButtonErrorState = createSelector(
    betslipSelector,
    selectQuickBetErrorsStateWithRewardsTokens,
    selectHasInsufficientMoneyError,
    selectQuickBetContext,
    (betslipState, errorStatus, hasInsufficientFunds, quickBetContext) => ({
        betslipState,
        errorStatus,
        hasInsufficientFunds,
        rewardTokenContext: quickBetContext,
    }),
);

@Injectable()
export class QuickBetActionButtonEffects {
    private errorsStatus: IQuickBetErrorsStatus;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private redirectHelperService: RedirectHelperService,
        private quickDepositService: QuickDepositService,
        private betslipConfig: BetslipConfig,
        private userService: UserService,
        private quickBetUtils: QuickBetUtils,
        private cashierService: CashierService,
        private betslipValidationService: BetslipErrorValidatorService,
        private quickDepositConfig: QuickDepositConfig,
        private epcotConfigService: EpcotConfigService,
    ) {}

    handleActionButtonClickEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.actionButtonClick),
            concatLatestFrom(() => [this.store.select(quickBetStateSelector), this.store.select(selectSlipErrorsState)]),
            concatMap(([action, quickbetState, slipErrors]) => {
                switch (action.payload.state) {
                    case QuickBetActionButtonState.AcceptChanges:
                        return quickbetState.hasClosedPick || quickbetState.hasLockedPick
                            ? [QuickBetActions.acceptMarketClosedChanges()]
                            : [QuickBetActions.acceptOddsChanges()];
                    case QuickBetActionButtonState.AcceptAndPlace:
                        return [
                            QuickBetActions.acceptOddsChanges(),
                            QuickBetActions.requestPlaceBet({
                                payload: { requestId: action.payload.requestId },
                            }),
                        ];
                    case QuickBetActionButtonState.AcceptAndPlaceFreeBet:
                        return [
                            QuickBetActions.acceptOddsChanges(),
                            QuickBetActions.requestPlaceBet({
                                payload: { requestId: action.payload.requestId },
                            }),
                        ];
                    case QuickBetActionButtonState.Place:
                        return [
                            QuickBetActions.requestPlaceBet({
                                payload: { requestId: action.payload.requestId },
                            }),
                        ];
                    case QuickBetActionButtonState.PlaceFreeBet:
                        return [
                            QuickBetActions.requestPlaceBet({
                                payload: { requestId: action.payload.requestId },
                            }),
                        ];
                    case QuickBetActionButtonState.Login:
                        return [QuickBetActions.login()];
                    case QuickBetActionButtonState.MakeDeposit:
                        return [QuickBetActions.makeDeposit()];
                    case QuickBetActionButtonState.DepositAndPlaceBet:
                        return [
                            QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.ProcessingDepositAndBet }),
                            QuickBetActions.depositAndBet({ requestId: action.payload.requestId }),
                        ];
                    case QuickBetActionButtonState.Deposit:
                        return [QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.ProcessingDeposit }), QuickBetActions.deposit()];
                    case QuickBetActionButtonState.UpdateMinStake:
                        const updateError = getStakeUpdateErrors(slipErrors);
                        if (updateError.length === 0) {
                            return [];
                        }
                        const stake = updateError[0].error.minimumStake.toString();

                        return [QuickBetActions.changeStake({ stake })];
                    default:
                        return [];
                }
            }),
        );
    });

    handleLoginEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.login),
                tap(() => this.redirectHelperService.goToLogin({ appendReferrer: true, useYahooLogin: true })),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleOpenQuickBetAndLoginEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.openQuickBet, UserActions.userLoggedIn),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            concatMap(([_, quickBetState]) => {
                if (quickBetState.keepOpen || this.userService.isAuthenticated) {
                    return [QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.Place })];
                }

                return [];
            }),
        );
    });

    handleMakeDepositEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.makeDeposit),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            concatLatestFrom(() => this.store.select(selectQuickBetErrorsStateWithRewardsTokens)),
            mergeMap(([[_, betslipState], errorStatus]) => {
                if (!this.quickDepositConfig.loadQuickDepositConfigOnLogin) {
                    return from(
                        this.quickDepositService.isQuickDepositEnabled().pipe(
                            concatMap(() => {
                                return this.loadQuickDeposit(betslipState, errorStatus);
                            }),
                        ),
                    );
                } else {
                    return this.loadQuickDeposit(betslipState, errorStatus);
                }
            }),
        );
    });

    handleDepositAndBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.depositAndBet),
            concatLatestFrom(() => this.store.select(selectActionButtonErrorState)),
            concatMap(([action, { betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext }]) => {
                this.quickDepositService.keepOpen = true;

                return this.quickDepositService.deposit().pipe(
                    concatMap((fundsAdded) => {
                        this.quickDepositService.keepOpen = false;
                        if (fundsAdded.success) {
                            const currentState = betslipState.quickBet.actionButtonState.state;
                            const betslipErrors = this.betslipValidationService.validatePreBetPlacement(betslipState);

                            if (
                                currentState === QuickBetActionButtonState.ProcessingDepositAndBet &&
                                !this.hasInsufficientFunds(betslipErrors) &&
                                fundsAdded.quickJourney
                            ) {
                                return [
                                    QuickBetActions.placeBet({
                                        payload: { requestId: action.requestId },
                                    }),
                                ];
                            }

                            return [];
                        }

                        return [this.getButtonStateAction(betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext)];
                    }),
                );
            }),
        );
    });

    handleDepositEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.deposit),
            switchMap(() => this.quickDepositService.deposit()),
            filter((funds) => funds.success),
            concatLatestFrom(() => this.store.select(selectActionButtonErrorState)),
            map(([_, { betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext }]) =>
                this.getButtonStateAction(betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext),
            ),
        );
    });

    handleBetslipErrorsCheckFinishedEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.betslipErrorsCheckFinished),
            concatLatestFrom(() => this.store.select(selectActionButtonErrorState)),
            concatMap(([_, { betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext }]) => {
                this.setErrorsStatus(errorStatus);

                if (betslipState.quickBet.actionButtonState.isProcessing) {
                    return [this.getProcessingButtonState(betslipState)];
                }

                return [this.getButtonStateAction(betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext)];
            }),
        );
    });

    handleQuickDepositCloseEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickDepositActions.quickDepositClose),
            concatLatestFrom(() => this.store.select(selectActionButtonErrorState)),
            map(([_, { betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext }]) =>
                this.getButtonStateAction(betslipState, errorStatus, hasInsufficientFunds, rewardTokenContext),
            ),
        );
    });

    handleCloseQuickDepositEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.updatePlaceBetResult, QuickBetActions.showTechnicalErrorDialog, QuickBetActions.showPickChangedWarning),
                tap(() => this.quickDepositService.close(true)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private getButtonStateAction(
        betslipState: IBetslipState,
        errorState: IQuickBetErrorsStatus,
        hasInsufficientFunds: boolean,
        rewardTokenContext: RewardTokenContext,
    ): Action {
        this.setErrorsStatus(errorState);

        if (!betslipState.quickBet.inOverlay && this.epcotConfigService.isEnabled() && this.errorsStatus.isClosed) {
            return QuickBetActions.closeQuickBet({ cleanErrors: false });
        }

        if (!this.userService.isAuthenticated) {
            return QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.Login });
        }

        if (betslipState.quickDeposit.isOpen) {
            return this.getDepositButtonState();
        }

        const depositWarning = betslipState.quickBet.betPlacedResult.warnings.some((w) => w.error.errorMessage.indexOf('makedeposit') !== -1);

        if (hasInsufficientFunds || depositWarning) {
            return QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.MakeDeposit });
        }

        if (this.errorsStatus.hasStakeUpdateErrors && this.betslipConfig.isMinStakeButtonEnabled) {
            return QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.UpdateMinStake });
        }

        const selectedToken = getSelectedTokenForContext(rewardTokenContext, betslipState.rewardTokens.tokens, betslipState.types);

        const isFreeBetSelected = selectedToken && isFreebetToken(selectedToken);

        const formerState = betslipState.quickBet.actionButtonState.state;
        let newState = QuickBetActionButtonState.Place;
        const oddsAccepted = this.quickBetUtils.areOddsAccepted(
            betslipState.picks.pickList,
            betslipState.quickBet.pickIds,
            betslipState.settings.oddsAcceptance,
        );
        if ((formerState === QuickBetActionButtonState.Place || formerState === QuickBetActionButtonState.PlaceFreeBet) && isFreeBetSelected) {
            newState = QuickBetActionButtonState.PlaceFreeBet;
        }

        if ((this.errorsStatus.oddsChanged && !this.errorsStatus.oddsAccepted) || !oddsAccepted) {
            if (this.betslipConfig.isAcceptAndPlaceBetEnabled) {
                newState = isFreeBetSelected ? QuickBetActionButtonState.AcceptAndPlaceFreeBet : QuickBetActionButtonState.AcceptAndPlace;
            } else if (!this.errorsStatus.isLocked) {
                newState = QuickBetActionButtonState.AcceptChanges;
            }
        }

        if (this.errorsStatus.isClosed || this.errorsStatus.isLocked) {
            newState = QuickBetActionButtonState.AcceptChanges;
        }

        return QuickBetActions.setActionButton({ payload: newState });
    }

    private getProcessingButtonState(betslipState: IBetslipState): Action {
        const state = betslipState.quickBet.actionButtonState.state;
        if (state === QuickBetActionButtonState.ProcessingDepositAndBet) {
            if (this.errorsStatus.isLocked || this.errorsStatus.oddsChanged) {
                return QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.ProcessingDeposit });
            }
        }

        return QuickBetActions.setActionButton({ payload: state });
    }

    private getDepositButtonState(): Action {
        let state: QuickBetActionButtonState;
        if (this.errorsStatus.isLocked || this.errorsStatus.oddsChanged || !this.betslipConfig.isDepositAndPlaceBetEnabled) {
            state = QuickBetActionButtonState.Deposit;
        } else {
            state = QuickBetActionButtonState.DepositAndPlaceBet;
        }

        return QuickBetActions.setActionButton({ payload: state });
    }

    private hasInsufficientFunds(betslipErrors: BetslipError[]): boolean {
        return betslipErrors.some((e) => isBetslipStatusInsufficientMoney(e));
    }

    private setErrorsStatus(errorStatus: IQuickBetErrorsStatus): void {
        this.errorsStatus = errorStatus;
    }

    private loadQuickDeposit(betslipState: IBetslipState, errorStatus: IQuickBetErrorsStatus): Action[] {
        const stake = Number(this.quickBetUtils.getStakeState(betslipState.types.singleBet).stake);

        if (this.quickDepositService.isEnabled) {
            this.quickDepositService.disableQuickDepositLoader();
            this.setErrorsStatus(errorStatus);
            this.quickDepositService.open(stake);

            return [this.getDepositButtonState()];
        } else {
            this.cashierService.goToCashierDeposit({});

            return [];
        }
    }
}
